// Spine Animation Support Type Definitions

// ============================================================================
// SPINE FILE TYPES
// ============================================================================

export type SpineVersion = '3.8' | '4.0' | '4.1' | '4.2';

export type SpineFileType = 'skeleton' | 'atlas' | 'texture';

export interface SpineFileInfo {
  id: string;
  file: File;
  name: string;
  extension: string;
  size: number;
  type: SpineFileType;
  isValid: boolean;
  error?: string;
}

export interface SpineFileSet {
  id: string;
  name: string; // Base name (e.g., "hero" from hero.skel, hero.atlas, hero.png)

  // Required files
  skeletonFile: SpineFileInfo | null;  // .skel (binary) or .json
  atlasFile: SpineFileInfo | null;     // .atlas
  textureFiles: SpineFileInfo[];       // .png (can be multiple for multi-page atlases)

  // Validation
  isComplete: boolean;
  isValid: boolean;
  errors: string[];
  warnings: string[];

  // Detected info (after parsing)
  version?: SpineVersion;
  boneCount?: number;
  slotCount?: number;
  skinNames?: string[];
  animationNames?: string[];

  // Import settings
  importScale: number;
  targetFolder: string;
  generatePrefab: boolean;
  prefabName?: string;
}

// ============================================================================
// SPINE ASSET (IMPORTED)
// ============================================================================

export interface SpineBone {
  name: string;
  parentName: string | null;
  length: number;
  rotation: number;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

export interface SpineSlot {
  name: string;
  boneName: string;
  color: string; // RGBA hex
  attachment: string | null;
  blendMode: 'normal' | 'additive' | 'multiply' | 'screen';
}

export interface SpineSkin {
  name: string;
  attachments: Record<string, SpineAttachment[]>;
}

export interface SpineAttachment {
  name: string;
  type: 'region' | 'mesh' | 'boundingbox' | 'path' | 'point' | 'clipping';
  slotName: string;
  // Region attachment
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  scaleX?: number;
  scaleY?: number;
}

export interface SpineAnimation {
  name: string;
  duration: number;
  boneTimelines: string[]; // Names of bones with keyframes
  slotTimelines: string[]; // Names of slots with keyframes
  hasEvents: boolean;
  eventNames: string[];
}

export interface SpineEvent {
  name: string;
  intValue: number;
  floatValue: number;
  stringValue: string;
  audioPath?: string;
  volume?: number;
  balance?: number;
}

export interface SpineAsset {
  id: string;
  name: string;
  path: string;

  // File references
  skeletonPath: string;
  atlasPath: string;
  texturePaths: string[];

  // Metadata
  version: SpineVersion;
  width: number;
  height: number;

  // Skeleton data
  bones: SpineBone[];
  slots: SpineSlot[];
  skins: SpineSkin[];
  animations: SpineAnimation[];
  events: SpineEvent[];

  // Import info
  importScale: number;
  importedAt: number;
}

// ============================================================================
// SPINE COMPONENT DATA
// ============================================================================

export interface SpineRendererData {
  // Asset reference
  spineAssetId: string | null;
  spineAssetPath: string | null;

  // Skin
  skinName: string;

  // Rendering
  tint: string;          // hex color
  alpha: number;         // 0-1
  premultipliedAlpha: boolean;

  // Sorting
  sortingLayer: string;
  sortingOrder: number;

  // Debug
  showBones: boolean;
  showSlots: boolean;
  showBoundingBoxes: boolean;
}

export interface SpineAnimatorData {
  // Playback
  playOnAwake: boolean;
  defaultAnimation: string;
  defaultLoop: boolean;
  speed: number;         // timeScale

  // Mixing
  defaultMixDuration: number;
  mixDurations: Record<string, Record<string, number>>; // from -> to -> duration

  // State
  currentAnimation: string;
  isPlaying: boolean;
  currentTime: number;

  // Events
  enableEvents: boolean;

  // Debug
  showAnimationList: boolean;
}

// ============================================================================
// SPINE IMPORT STATE
// ============================================================================

export interface SpineImportState {
  // Dialog
  isOpen: boolean;

  // Files
  fileSets: SpineFileSet[];
  selectedFileSetId: string | null;

  // Preview
  previewAsset: Partial<SpineAsset> | null;
  previewAnimation: string | null;
  isPreviewPlaying: boolean;

  // Import progress
  isImporting: boolean;
  importProgress: {
    current: number;
    total: number;
    currentFile: string;
    status: 'idle' | 'validating' | 'parsing' | 'copying' | 'complete' | 'error';
  };

  // Results
  importedAssets: SpineAsset[];
  errors: string[];
}

export interface SpineImportActions {
  // Dialog
  openDialog: () => void;
  closeDialog: () => void;

  // File management
  addFiles: (files: FileList | File[]) => void;
  removeFileSet: (id: string) => void;
  clearFiles: () => void;
  selectFileSet: (id: string | null) => void;

  // Settings per file set
  updateFileSet: (id: string, updates: Partial<SpineFileSet>) => void;
  setImportScale: (id: string, scale: number) => void;
  setTargetFolder: (id: string, folder: string) => void;
  setGeneratePrefab: (id: string, generate: boolean) => void;

  // Preview
  setPreviewAnimation: (name: string | null) => void;
  togglePreviewPlaying: () => void;

  // Import
  validateFiles: () => Promise<void>;
  startImport: () => Promise<void>;
  cancelImport: () => void;

  // Project panel integration
  addImportedAsset: (asset: SpineAsset) => void;
}

// ============================================================================
// SPINE RUNTIME STATE
// ============================================================================

export interface SpineInstance {
  id: string;
  gameObjectId: string;
  assetId: string;

  // Runtime state (for play mode)
  skeleton: unknown;      // Spine.Skeleton
  animationState: unknown; // Spine.AnimationState

  // Playback state
  currentAnimation: string | null;
  currentSkin: string;
  timeScale: number;
  isPlaying: boolean;
}

export interface SpineRuntimeState {
  instances: Record<string, SpineInstance>;
  loadedAssets: Record<string, SpineAsset>;
}

export interface SpineRuntimeActions {
  // Instance management
  createInstance: (gameObjectId: string, assetId: string) => string;
  destroyInstance: (instanceId: string) => void;

  // Asset loading
  loadAsset: (assetId: string) => Promise<SpineAsset>;
  unloadAsset: (assetId: string) => void;

  // Animation control
  play: (instanceId: string, animationName: string, loop?: boolean) => void;
  stop: (instanceId: string) => void;
  pause: (instanceId: string) => void;
  resume: (instanceId: string) => void;
  setTimeScale: (instanceId: string, scale: number) => void;

  // Skin control
  setSkin: (instanceId: string, skinName: string) => void;

  // Mixing
  setMixDuration: (instanceId: string, fromAnim: string, toAnim: string, duration: number) => void;

  // Events
  onEvent: (instanceId: string, callback: (event: SpineEvent) => void) => () => void;
  onComplete: (instanceId: string, callback: (animation: string) => void) => () => void;

  // Project panel integration
  registerAssetFromProject: (
    name: string,
    skeletonPath: string,
    atlasPath: string,
    texturePaths: string[],
    metadata?: Partial<SpineAsset>
  ) => SpineAsset;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_SPINE_RENDERER_DATA: SpineRendererData = {
  spineAssetId: null,
  spineAssetPath: null,
  skinName: 'default',
  tint: '#ffffff',
  alpha: 1,
  premultipliedAlpha: true,
  sortingLayer: 'default',
  sortingOrder: 0,
  showBones: false,
  showSlots: false,
  showBoundingBoxes: false,
};

export const DEFAULT_SPINE_ANIMATOR_DATA: SpineAnimatorData = {
  playOnAwake: false,
  defaultAnimation: '',
  defaultLoop: true,
  speed: 1,
  defaultMixDuration: 0.2,
  mixDurations: {},
  currentAnimation: '',
  isPlaying: false,
  currentTime: 0,
  enableEvents: true,
  showAnimationList: true,
};

export const DEFAULT_IMPORT_SCALE = 1;

// ============================================================================
// SPINE VERSION COMPATIBILITY
// ============================================================================

export const SPINE_VERSION_INFO: Record<SpineVersion, {
  name: string;
  runtimeVersion: string;
  supported: boolean;
}> = {
  '3.8': { name: 'Spine 3.8', runtimeVersion: '3.8.x', supported: true },
  '4.0': { name: 'Spine 4.0', runtimeVersion: '4.0.x', supported: true },
  '4.1': { name: 'Spine 4.1', runtimeVersion: '4.1.x', supported: true },
  '4.2': { name: 'Spine 4.2', runtimeVersion: '4.2.x', supported: true },
};

// ============================================================================
// FILE EXTENSIONS
// ============================================================================

export const SPINE_SKELETON_EXTENSIONS = ['.skel', '.json'];
export const SPINE_ATLAS_EXTENSION = '.atlas';
export const SPINE_TEXTURE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function detectSpineFileType(file: File): SpineFileType | null {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();

  if (SPINE_SKELETON_EXTENSIONS.includes(ext)) return 'skeleton';
  if (ext === SPINE_ATLAS_EXTENSION) return 'atlas';
  if (SPINE_TEXTURE_EXTENSIONS.includes(ext)) return 'texture';

  return null;
}

export function getBaseNameFromFile(fileName: string): string {
  // Remove extension and common suffixes
  let baseName = fileName.replace(/\.[^/.]+$/, '');

  // Remove common texture suffixes like _1, _2, etc.
  baseName = baseName.replace(/_\d+$/, '');

  return baseName;
}

export function validateSpineFileSet(fileSet: SpineFileSet): string[] {
  const errors: string[] = [];

  if (!fileSet.skeletonFile) {
    errors.push('Missing skeleton file (.skel or .json)');
  }

  if (!fileSet.atlasFile) {
    errors.push('Missing atlas file (.atlas)');
  }

  if (fileSet.textureFiles.length === 0) {
    errors.push('Missing texture file(s) (.png)');
  }

  return errors;
}
